// src/pages/SacredCircuitryLeadMagnet.js

import React, { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  SparklesIcon,
  CheckCircleIcon,
  PlayCircleIcon,
  ArrowDownTrayIcon,
  GiftIcon,
  RectangleStackIcon,
  ChartBarIcon,
  FilmIcon,
  SpeakerWaveIcon,
  QuestionMarkCircleIcon, // Icono para el botón del popup
  XMarkIcon, // Icono para cerrar el popup
} from '@heroicons/react/24/solid';
import { useLocation } from 'react-router-dom';
import UrlConfig from '../../data/UrlConfig';
import './SacredCircuitryLeadMagnet.css';

// --- Imports del componente Deck de una sola carta diaria ---
import SacredCircuitryDeck from '../../components/decks/SacredCircuitryDeck'; // Ajusta la ruta si es necesario

// --- Imports de las imágenes de los símbolos ---
import symbol1 from '../../assets/bashar/sacred-circuitry/deck/1.png';
import symbol2 from '../../assets/bashar/sacred-circuitry/deck/2.png';
import symbol3 from '../../assets/bashar/sacred-circuitry/deck/3.png';
import symbol4 from '../../assets/bashar/sacred-circuitry/deck/4.png';
import symbol5 from '../../assets/bashar/sacred-circuitry/deck/5.png';
import symbol6 from '../../assets/bashar/sacred-circuitry/deck/6.png';
import symbol7 from '../../assets/bashar/sacred-circuitry/deck/7.png';
import symbol8 from '../../assets/bashar/sacred-circuitry/deck/8.png';
import symbol9 from '../../assets/bashar/sacred-circuitry/deck/9.png';
import symbol10 from '../../assets/bashar/sacred-circuitry/deck/10.png';
import symbol11 from '../../assets/bashar/sacred-circuitry/deck/11.png';
import symbol12 from '../../assets/bashar/sacred-circuitry/deck/12.png';
import symbol13 from '../../assets/bashar/sacred-circuitry/deck/13.png';
import symbol14 from '../../assets/bashar/sacred-circuitry/deck/14.png';
import symbol15 from '../../assets/bashar/sacred-circuitry/deck/15.png';
import { useAuth } from '../../context/AuthContext'; // Importación de useAuth
import CommentSection from '../../components/comments/CommentSection';

const API_URLS = UrlConfig;
const sendCodedLog = (log) => console.log("Logging:", log);
const Common = { getCommonLogData: (location) => ({ page: location.pathname, timestamp: new Date().toISOString() }) };

// Se mantienen aquí porque se pasarán como props al componente de deck.
const symbolImages = [
  symbol1, symbol2, symbol3, symbol4, symbol5,
  symbol6, symbol7, symbol8, symbol9, symbol10,
  symbol11, symbol12, symbol13, symbol14, symbol15
];

const sacredCircuits = [
    { id: 1, name: 'Center', description: 'Represents grounding and alignment with your inner self and universal energy.' },
    { id: 2, name: 'Expand', description: 'Encourages the expansion of your consciousness and the discovery of new horizons.' },
    { id: 3, name: 'Balance', description: 'Symbolizes the need to find equilibrium in all aspects of life.' },
    { id: 4, name: 'Prepare', description: 'Encourages preparation and organization for upcoming changes or opportunities.' },
    { id: 5, name: 'Express', description: 'Supports clear communication and authentic expression of one\'s truth.' },
    { id: 6, name: 'Extend', description: 'Reach out with your energy, connecting with the broader universe and those around you.' },
    { id: 7, name: 'Unlock', description: 'Open the doors of perception, freeing yourself from limiting beliefs and mental constraints.' },
    { id: 8, name: 'Flow', description: 'Surrender to the natural flow of energy, allowing life to unfold effortlessly around you.' },
    { id: 9, name: 'Receive', description: 'Open your being to the gifts of the universe, accepting abundance and wisdom.' },
    { id: 10, name: 'Relate', description: 'Connect deeply with others and the world, fostering understanding and compassion.' },
    { id: 11, name: 'Realize', description: 'Achieve a state of enlightenment, recognizing the truth of your existence and your purpose.' },
    { id: 12, name: 'Act', description: 'Take inspired action in alignment with your highest self, manifesting your intentions.' },
    { id: 13, name: 'Transform', description: 'Embrace change and growth, evolving into a more authentic version of yourself.' },
    { id: 14, name: 'Connect', description: 'Strengthen your bond with the divine and the cosmos, experiencing unity with all that is.' },
    { id: 15, name: 'Manifest', description: 'Bring your dreams and desires into reality, making the invisible visible.' },
];

const SacredCircuitryLeadMagnet = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState(localStorage.getItem('track_user_email') || '');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false); // Estado para el popup
  const { user } = useAuth(); // Obtiene el objeto user del contexto de autenticación

  const location = useLocation();

  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      setIsLoading(false);
      return;
    }

    localStorage.setItem('track_user_email', email.trim());
    name && localStorage.setItem('track_user_name', name.trim());

    try {
      const response = await fetch(API_URLS.leads_action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim(),
          folder: 'sacred-circuitry-full-kit',
          page: location.pathname,
          lang: 'en',
          newsletter: ['sacred-circuitry-full-kit'],
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
        sendCodedLog({
          message: 'Sacred Circuitry Full Kit form submitted successfully',
          event: 'sacred_circuitry_success_en',
          ...Common.getCommonLogData(location)
        });
      } else {
        throw new Error(data.message || 'An error occurred.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative bg-black text-white min-h-screen font-sans flex flex-col">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-black to-gray-900 opacity-95 z-0"></div>

      <main ref={ref} className={`relative z-10 flex-grow container mx-auto px-4 py-12 flex flex-col items-center ${inView ? "fade-in-animation" : "opacity-0"}`}>

        <h1 className="text-4xl sm:text-5xl font-bold text-center mb-4 leading-tight">
          Activate Your Potential with Sacred Circuitry
        </h1>
        <p className="text-lg sm:text-xl text-gray-400 text-center max-w-3xl mb-12">
          A gift from Bashar's civilization to help you rewire your brain, connect with your Higher Mind, and manifest your desired reality.
        </p>
        
        <div className="w-full max-w-4xl mx-auto mb-16 p-8 bg-gray-900/50 border border-sky-700/50 rounded-xl shadow-2xl shadow-sky-500/20 backdrop-blur-sm">
            <h2 className="text-3xl font-bold text-center mb-6">Your Free Tools for Transformation</h2>
            <div className="flex flex-col md:flex-row justify-center items-center gap-6">
                <a
                    href="https://gusaguiar.com/assets/sacred/circuits.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="flex items-center gap-3 py-3 px-6 bg-sky-600 hover:bg-sky-700 rounded-lg font-semibold transition-all duration-300"
                >
                    <ArrowDownTrayIcon className="w-6 h-6"/>
                    Download the 15 Symbols PDF
                </a>
                <p className="text-gray-400">or</p>
                <a
                    href="#meditation-video"
                    className="flex items-center gap-3 py-3 px-6 bg-purple-600 hover:bg-purple-700 rounded-lg font-semibold transition-all duration-300"
                >
                    <PlayCircleIcon className="w-6 h-6"/>
                    Watch the 15-Min Meditation
                </a>
            </div>
        </div>

        {/* --- INSERCIÓN DEL COMPONENTE SacredCircuitryDeck para 1 carta diaria --- */}
        <SacredCircuitryDeck
            sacredCircuits={sacredCircuits}
            symbolImages={symbolImages}
            maxDrawsPerDay={1}
        />
        {/* --- FIN DE LA INSERCIÓN --- */}

        <div id="meditation-video" className="w-full max-w-4xl mx-auto mb-16 scroll-mt-20">
            <h2 className="text-3xl font-bold text-center mb-8">The 15-Minute Sacred Circuitry Meditation</h2>
            <div className="aspect-video w-full rounded-lg overflow-hidden shadow-2xl shadow-purple-500/20 border border-purple-700">
                <iframe
                    src="https://www.youtube.com/embed/xHZAfb9FE8Q"
                    title="Bashar Sacred Circuitry Meditation"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                ></iframe>
            </div>

            {/* --- BOTÓN PARA ABRIR EL POPUP DEL VIDEO EXPLICATIVO (MOVIDO AQUÍ) --- */}
            <div className="mt-8 flex justify-center">
                <button
                    onClick={() => setIsPopupOpen(true)}
                    className="flex items-center gap-3 py-3 px-6 bg-gray-800 hover:bg-gray-700 rounded-lg font-semibold transition-all duration-300 border border-gray-600 shadow-lg"
                >
                    <QuestionMarkCircleIcon className="w-6 h-6 text-sky-400"/>
                    <span>How to use these tools? (Watch Instructions)</span>
                </button>
            </div>
        </div>

        {/* Sección de beneficios y formulario, ahora condicionalmente renderizada */}
        {!user && ( // Solo muestra esta sección si NO hay un usuario logueado
            <div className="w-full max-w-4xl flex flex-col md:flex-row gap-8 items-center bg-gray-900/50 border border-purple-700/50 rounded-xl p-8 shadow-2xl shadow-purple-500/20 backdrop-blur-sm">
                <div className="w-full md:w-1/2">
                    <h3 className="font-bold text-2xl mb-4 text-sky-400">Ready for the Full Experience?</h3>
                    <p className="mb-6 text-gray-300">Sign up to unlock the complete, members-only "Sacred Circuitry Toolkit" and gain access to these powerful resources:</p>
                    <ul className="space-y-4">
                        <li className="flex items-start gap-3"><RectangleStackIcon className="w-6 h-6 text-purple-400 mt-1 flex-shrink-0" /><span><strong>Full Black Oracle Deck:</strong> A stunning, high-resolution printable deck of all 15 symbols.</span></li>
                        <li className="flex items-start gap-3"><ChartBarIcon className="w-6 h-6 text-sky-400 mt-1 flex-shrink-0" /><span><strong>Save Your Progress:</strong> Log in to save your daily card draws and track your journey of transformation.</span></li>
                        <li className="flex items-start gap-3"><FilmIcon className="w-6 h-6 text-pink-400 mt-1 flex-shrink-0" /><span><strong>Full 6-Hour Transmission:</strong> Get access to the complete, unabridged "Sacred Circuitry" session from Bashar.</span></li>
                        <li className="flex items-start gap-3"><SpeakerWaveIcon className="w-6 h-6 text-green-400 mt-1 flex-shrink-0" /><span><strong>Set of Bashar Meditations:</strong> A curated collection of powerful guided meditations to accelerate your growth.</span></li>
                        <li className="flex items-start gap-3"><GiftIcon className="w-6 h-6 text-yellow-400 mt-1 flex-shrink-0" /><span><strong>And much more</strong> to support your path of awakening.</span></li>
                    </ul>
                </div>

                <div className="w-full md:w-1/2 bg-black/30 border border-gray-700 rounded-xl p-6 sm:p-8">
                {submitted ? (
                    <div className="text-center text-green-400 p-4 h-full flex flex-col justify-center items-center">
                    <CheckCircleIcon className="w-16 h-16 mx-auto mb-4" />
                    <p className="text-2xl font-semibold text-white">Success!</p>
                    <p className="text-lg">Your full Toolkit has been sent to your inbox. The journey continues!</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="flex flex-col space-y-5">
                    <h3 className="font-semibold text-lg text-center">Get Your Free Toolkit Now</h3>
                    <div>
                        <label htmlFor="name" className="block text-gray-300 text-sm font-medium mb-2">Your Name (Optional)</label>
                        <input type="text" id="name" value={name} onChange={(e) => setName(e.target.value)} className="w-full p-3 rounded-md bg-gray-900/70 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-sky-400" placeholder="So we can greet you properly"/>
                    </div>
                    <div>
                        <label htmlFor="email" className="block text-gray-300 text-sm font-medium mb-2">Your Best Email</label>
                        <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full p-3 rounded-md bg-gray-900/70 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-sky-400" placeholder="Where we'll send your gifts" required/>
                    </div>
                    {error && <p className="text-sm text-center text-red-400">{error}</p>}
                    <button type="submit"
                    className="w-full text-lg py-3 px-4 rounded-xl bg-gradient-to-r from-purple-600 to-sky-500 font-semibold hover:from-purple-700 hover:to-sky-600 transition-all duration-300 disabled:opacity-50" disabled={isLoading}>
                        {isLoading ? 'Sending...' : 'Unlock My Full Toolkit!'}
                    </button>
                    </form>
                )}
                </div>
            </div>
        )}

        {user && ( // Muestra un mensaje diferente si SÍ hay un usuario logueado
            <div className="w-full max-w-4xl bg-gray-900/50 border border-green-700/50 rounded-xl p-8 shadow-2xl shadow-green-500/20 backdrop-blur-sm mt-16">
                <div className="text-center text-green-400 p-4 h-full flex flex-col justify-center items-center">
                    <CheckCircleIcon className="w-16 h-16 mx-auto mb-4" />
                    <p className="text-2xl font-semibold text-white">Welcome, {user.name || user.email || 'member'}!</p>
                    <p className="text-lg mt-2 text-gray-300">You already have access to the complete Sacred Circuitry Toolkit. Dive in and explore!</p>
                </div>
            </div>
        )}

        {/* --- POPUP DEL VIDEO EXPLICATIVO --- */}
        {isPopupOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50 p-4"
            onClick={() => setIsPopupOpen(false)} // Cierra el popup al hacer clic en el fondo
          >
            <div 
              className="relative bg-black rounded-lg shadow-xl w-full max-w-3xl border border-sky-700/50"
              onClick={(e) => e.stopPropagation()} // Evita que el clic dentro del video cierre el popup
            >
              <button
                onClick={() => setIsPopupOpen(false)}
                className="absolute -top-3 -right-3 w-9 h-9 bg-white text-black rounded-full flex items-center justify-center z-50 hover:bg-gray-300 transition-colors"
                aria-label="Close video"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
              <div className="aspect-video w-full">
                <iframe
                  src="https://www.youtube.com/embed/N4eWSzEHUp0?autoplay=1&rel=0"
                  title="What is Sacred Circuitry? Explainer Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full rounded-lg"
                ></iframe>
              </div>
            </div>
          </div>
        )}

      </main>

      <footer className="relative z-10 text-center py-6 text-xs text-gray-600">
        © {new Date().getFullYear()} Gus • Aguiar | On a mission to uplift and transform.
      </footer>
    </div>
  );
};

export default SacredCircuitryLeadMagnet;